import { Brick, registerBrick } from 'olympe'
import ReactDOM from 'react-dom'
import React from 'react'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import { Button, CircularProgress } from '@mui/material'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import { getIcon } from '../shared/utils/_icons.jsx'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import IconButton from '@mui/material/IconButton'
import {clearFileForAllRows, formatErrorMessage, updateNoBcp, uploadFileForMultipleRows} from './NoBcp'
import { FileUploadComponent } from '../shared/FileUploadComponent.jsx';
import {isMaterialEligibleForRF, reasonCodes, spendCategoriesForRF} from './_enums';
import Tooltip from '@mui/material/Tooltip';
import {ErrorFlow} from 'olympe';
import {RFDocumentTemplateLink} from './_snippets.jsx';

export default class MultipleEditPopup extends Brick {
    /**
     * Executed every time the brick receives a new trigger event.
     * Note that the method will be executed _even if_ an input (data) is undefined.
     *
     * @protected
     * @param {BrickContext} context
     * @param {*} data
     * @param {function()} forwardEvent
     * @param {function(ErrorFlow)} setErrorFlow
     * @param {function(boolean)} setUpdateDone
     */
    onUpdate(context, [data], [forwardEvent, setErrorFlow, setUpdateDone]) {
        const MultipleEditPopupId = 'multipleEditPopup017be88e50b7e689d1e1'
        if (!document.getElementById(MultipleEditPopupId)) {
            const head = document.body
            const multipleEditPopup = document.createElement('div')
            multipleEditPopup.id = MultipleEditPopupId
            head.appendChild(multipleEditPopup)
        }
        setUpdateDone(false)
        ReactDOM.render(
            <MultipleEditPopup.Component
                data={data}
                forwardEvent={forwardEvent}
                setUpdateDone={setUpdateDone}
                throwError={setErrorFlow}
            />,
            document.getElementById(MultipleEditPopupId)
        )
    }
}

MultipleEditPopup.Component = props => {
    const [open, setOpen] = React.useState(true)
    const [selectedReasonCode, setSelectedReasonCode] = React.useState('AM')
    const [comment, setComment] = React.useState('')
    const [expiryDate, setExpiryDate] = React.useState(new Date())
    const [loading, setLoading] = React.useState(false)
    const [file, setFile] = React.useState(null);

    let currentDate = new Date()
    const expiryDatePlus6Months = new Date(currentDate.setMonth(currentDate.getMonth() + 6))
    currentDate = new Date()
    const expiryDatePlus1Year = new Date(currentDate.setMonth(currentDate.getMonth() + 12))

    const expiryDates = [
        {
            value: expiryDatePlus6Months,
            label: '6 months',
        },
        {
            value: expiryDatePlus1Year,
            label: '1 year',
        },
    ]

    const handleClose = () => {
        setOpen(false);
        const el = document.getElementById('multipleEditPopup017be88e50b7e689d1e1');
        if(el !== null) {
            el.remove();
        }
        props.forwardEvent()
    }

    const handleClick = label => {
        setSelectedReasonCode(label)
    }
    // Check status of button
    const checkStatusOfButton = (selectedReasonCode, file) =>{
      let disabledButtonStatus;
      if(selectedReasonCode ==='RF' && null === file){
        disabledButtonStatus = true
      } else if(selectedReasonCode ==='AM' || selectedReasonCode ==='BD' ||selectedReasonCode ==='UM') {
        disabledButtonStatus = false
      }
      return disabledButtonStatus;
    }

    const handleChange = event => {
        setExpiryDate(event.target.value)
    }

    const handleEditReasonCode = async () => {
        const checkedItems = props.data
        .filter((item) => item.checked)
        .map((item) => {
          item.reasonCode = selectedReasonCode;
          item.expiry = expiryDate;
          item.comments = comment;
          return item;
            });

        setLoading(true);

       if (file) {
         const errorMessage = await uploadFileForMultipleRows(
           checkedItems,
           file
         );
         if (errorMessage !== null) {
                setLoading(false);
                props.throwError(ErrorFlow.create(errorMessage, 1));
                return;
            }
        }

      const result = await updateNoBcp(checkedItems);
      setLoading(false);
        if (result.error) {
        props.throwError(
          ErrorFlow.create(
            formatErrorMessage(
              result,
              'An error occurred while updating the record'
            ),
            1
          )
        );
        } else {
            props.setUpdateDone(true);
            handleClose();
        }
    }

    let canBeRF = true;
    props.data.forEach((row) => {
        if (!isMaterialEligibleForRF(row)) {
            canBeRF = false;
        }
    });
    const orderedReasonCodes = [reasonCodes['AM'], reasonCodes['BD'], reasonCodes['UM'], reasonCodes['RF']];
    const validRFCategories = spendCategoriesForRF.join(', ');
    const reasonCodeText = {
        'UM': <>The material cannot be sourced from an alternative vendor because it does not exist in the market & it is not possible to develop an alternative for the material as no other vendors are capable. It could also be linked to a contractual or IP issue.</>,
        'BD': <>Alternative exists but business decision is to not incur the cost associated with new CAPEX, qualification or resource required. This decision is fully aligned with business & signed off by market NimCom.</>,
        'RF': <>Usage of this reason code only limited to categories in scope* and only for materials/specification that is able to be approved within 21 calendar days the longest.<br />The VAM factory usage approval of BCP confirmed for same level as for the initial material must also be available.<br />Readiness of vendor site for fast-track approval is done at Plant-material(or Specification for packaging)-Vendor site combination level and  be signed off by QM, PRO and AG. <u>This approval must be downloaded, signed by all 3 functions, uploaded / documented as proof in the readiness.</u><br/><br/>*Categories in scope: Printed packaging from {validRFCategories} categories</>,
        'AM': <>Different material/specification that can be used as a back-up. The alternative material should have an active QIR. The usage of this reason code must be reviewed and approved by relevant application group. The BP capturing this reason code must keep traceability of this formal approval.</>,
    };

    return (
      <Dialog
        onClose={handleClose}
        open={open}
        aria-labelledby='multiple-edit-popup-title'
        style={{ flexDirection: 'row' }}>
        <IconButton
          aria-label='close'
          style={{ marginLeft: 'auto' }}
          onClick={handleClose}
          size='large'>
          {getIcon('Close')}
        </IconButton>
        {loading ? (
          <DialogContent
            style={{
              padding: '12px 70px 60px 70px',
              height: '150px',
              width: '100px',
            }}>
            <CircularProgress
              style={{
                position: 'absolute',
                top: '48%',
                left: '48%',
                width: '20%',
                height: 'auto',
              }}
            />
          </DialogContent>
        ) : (
          <DialogContent style={{ padding: '12px 70px 60px 70px' }}>
            <p
              id='multiple-edit-popup-title'
              className={'multiple-edit-popup-title'}>
              Would you like to change the status?
            </p>
            <div className={'multiple-edit-popup-mark-as'}>
              <div className={'multiple-edit-popup-subtitle'}>Mark as</div>
              {orderedReasonCodes.map((opt) => {
                const disabled = opt.id === 'RF' && !canBeRF;
                const chip = (
                  <Chip
                    icon={getIcon(
                      selectedReasonCode === opt.id
                        ? 'CheckCircleOutline'
                        : 'RadioButtonUnchecked',
                      true,
                      { style: { color: 'white', fontSize: '1rem' } }
                    )}
                    key={opt.id}
                    label={opt.id}
                    onClick={() => handleClick(opt.id)}
                    style={{ backgroundColor: opt.color, color: 'white' }}
                    disabled={disabled}
                  />
                );
                if (disabled) {
                  // need to wrap chip in a div to workaround the chip not firing events (because disabled)
                  return (
                    <Tooltip
                      title={
                        <>
                          Some of the rows selected are not eligible for RF.
                          <br />
                          The categories of the selected material must be{' '}
                          {validRFCategories}.
                        </>
                      }
                      placement='top'
                      arrow>
                      <div>{chip}</div>
                    </Tooltip>
                  );
                } else {
                  return chip;
                }
              })}
            </div>
            <p className={'multiple-edit-popup-description-title'}>
              {reasonCodes[selectedReasonCode]?.description}
            </p>
            <p
              className={'multiple-edit-popup-description-text'}
              style={{ minHeight: '90px' }}>
              {reasonCodeText[selectedReasonCode]}
            </p>
            <div className={'multiple-edit-popup-expiry'}>
              <div className={'multiple-edit-popup-subtitle'}>Next Review Date *</div>
              <Select
                renderValue={(value) => value.toISOString().split('T')[0]}
                value={expiryDate}
                onChange={handleChange}>
                {expiryDates.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div
              className={'multiple-edit-popup-subtitle'}
              style={{ marginBottom: '5px' }}>
              Description *
            </div>
            <TextField
              id='outlined-multiline-textField'
              placeholder={'Type a description'}
              multiline
              rows={4}
              variant='outlined'
              fullWidth
              onChange={(event) => setComment(event.target.value)}
            />
            <>
              <div
                className={'multiple-edit-popup-subtitle'}
                style={{ marginBottom: '5px' }}>
                Document {selectedReasonCode === 'RF' && '*'}
              </div>
              <RFDocumentTemplateLink style={{ marginBottom: '5px' }} />
              <FileUploadComponent
                file={file}
                setFile={setFile}
                style={{ height: '100px' }}
              />
            </>

            <div className={'popup-save-button-container'}>
              <Button
                className={'popup-save-button'}
                variant={'contained'}
                disabled={
                  !comment ||
                  checkStatusOfButton(selectedReasonCode, file) ||
                  (expiryDate.getMonth() === new Date().getMonth() &&
                    expiryDate.getFullYear() === new Date().getFullYear())
                }
                sx={{
                  '&.Mui-disabled': {
                    background: '#0000001f !important',
                    color: '#00000042 !important',
                  },
                }}
                onClick={handleEditReasonCode}>
                Yes, I want to change the reason code
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    );
}

registerBrick('017be88e50b7e689d1e1', MultipleEditPopup)
